/**
 * Check what the server actually sent against the generated row schemas.
 *
 * WHY THIS IS NOT `.parse()`
 * --------------------------
 * Rejecting on mismatch would turn every contract drift into an outage. The
 * failure this guards is the opposite of loud: a renamed column, or a field
 * that starts arriving null, reaches the client as `undefined`, the falsy
 * branch runs, and a page renders empty. No error, no Sentry event, nothing to
 * notice.
 *
 * NOT the same bug as `programs.status`. There the CLIENT invented a field
 * and the server was correct, so this check would have passed it — the
 * generated types in schema/tables.ts are what make that a compile error.
 * The two guard opposite directions of the same contract, which is why both
 * exist.
 *
 * So this REPORTS and returns the data unchanged. The data path is exactly as
 * it was; what changes is that the mismatch now has a name, a field, and a
 * Sentry event, instead of being a blank screen someone eventually mentions.
 *
 * Turning a swallow into a rejection is a breaking change for every caller —
 * this repo has the scars — so it stays observational until the reports say
 * what rejecting would actually cost.
 */
import type { ZodType } from "zod";
import { captureError } from "../utils/sentry";

/** Cap on reported issues per response: one bad row usually means all of them. */
const MAX_ISSUES = 5;

/** Same (endpoint, field, code) reported once per session, not per request. */
const reported = new Set<string>();

export interface ValidateOptions {
  /** Endpoint or operation name — the tag you will search Sentry by. */
  operation: string;
}

/**
 * Validate a single object. Always returns `value`, mismatch or not.
 */
export function validateShape<T>(
  schema: ZodType,
  value: T,
  { operation }: ValidateOptions
): T {
  // Nothing to check, and "no data" is a separate concern with its own handling.
  if (value === null || value === undefined) return value;

  const result = schema.safeParse(value);
  if (result.success) return value;

  for (const issue of result.error.issues.slice(0, MAX_ISSUES)) {
    const field = issue.path.join(".") || "(root)";
    const key = `${operation}:${field}:${issue.code}`;
    if (reported.has(key)) continue;
    reported.add(key);

    captureError(
      new Error(
        `Response shape mismatch in ${operation}: ${field} — ${issue.message}`
      ),
      {
        component: "api.validateResponse",
        operation,
        field,
        issueCode: issue.code,
      },
      // Warning, not error: the request succeeded and the caller got its data.
      // What is wrong is the contract, and that is a thing to fix deliberately,
      // not to page someone about at 2am.
      "warning"
    );
  }

  return value;
}

/**
 * Validate every element of a list. Returns the list unchanged.
 */
export function validateList<T>(
  schema: ZodType,
  values: T[] | null | undefined,
  options: ValidateOptions
): T[] | null | undefined {
  if (!Array.isArray(values)) return values;
  // One report per distinct problem is the point; checking every row would
  // find the same issue N times and the dedupe would discard N-1 of them.
  // Checking the first is enough to notice, and cheap on a long list.
  if (values.length) validateShape(schema, values[0], options);
  return values;
}

/** Test seam: the dedupe set is module state and would leak between cases. */
export function __resetReportedForTests(): void {
  reported.clear();
}
